import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import ManhwaCard from '@/components/feature/ManhwaCard';
import Pagination from '@/components/feature/Pagination';
import { manhwas } from '@/mocks/manhwas';
import { categories, categoryMap } from '@/mocks/categories';

const ITEMS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || 'all';
  const [currentPage, setCurrentPage] = useState(1);

  const filteredManhwas = useMemo(() => {
    if (activeSlug === 'all') return manhwas;
    if (activeSlug === 'hot') return [...manhwas].sort((a, b) => b.rating - a.rating);
    return manhwas.filter((m) => m.categorySlug === activeSlug);
  }, [activeSlug]);

  const totalPages = Math.ceil(filteredManhwas.length / ITEMS_PER_PAGE);
  const paginatedManhwas = filteredManhwas.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handleCategoryChange = (newSlug: string) => {
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  };

  const categoryName = categoryMap[activeSlug] || '全部';
  const pageTitle = activeSlug === 'all'
    ? '韩漫分类大全_全部韩国漫画_韩漫屋'
    : `${categoryName}韩国漫画_${categoryName}韩漫在线观看_韩漫屋`;
  const pageDesc = activeSlug === 'all'
    ? '韩漫屋提供海量韩漫分类大全，涵盖恋爱、奇幻、动作、悬疑等多种类型的好看韩国漫画，全部免费在线观看，每日更新最新章节。'
    : `韩漫屋${categoryName}分类收录精选${categoryName}韩漫，提供高清${categoryName}韩国漫画免费在线观看，每日同步更新最新章节。`;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: activeSlug === 'all' ? '全部漫画' : categoryName },
  ];

  const schemaData = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${categoryName} - 韩漫屋`,
      description: pageDesc,
      url: `https://www.hanmanwu.com/category/${activeSlug}`,
      inLanguage: 'zh-CN',
    },
  ];

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={`韩漫屋,${categoryName},韩国漫画,韩漫在线观看,免费韩漫,好看韩国漫画`}
        canonicalUrl={`https://www.hanmanwu.com/category/${activeSlug}`}
        ogType="website"
        schemaJsonLd={schemaData}
      />

      <main className="min-h-screen pt-20 pb-10">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />

          <div className="px-4 md:px-6 mb-6">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">
              {categoryName}
              <span className="text-base font-normal text-foreground-500 ml-3">
                共 {filteredManhwas.length} 部作品
              </span>
            </h1>
          </div>

          <div className="px-4 md:px-6 mb-8">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap" role="tablist" aria-label="漫画分类筛选">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => handleCategoryChange(cat.slug)}
                  role="tab"
                  aria-selected={activeSlug === cat.slug}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {paginatedManhwas.length > 0 ? (
            <>
              <div className="px-4 md:px-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {paginatedManhwas.map((m) => (
                    <ManhwaCard key={m.id} manhwa={m} />
                  ))}
                </div>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </>
          ) : (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300 block mb-4"></i>
              <p className="text-foreground-500">该分类下暂无漫画，请浏览其他分类</p>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 mt-4 px-6 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
              >
                查看全部漫画
              </Link>
            </div>
          )}
        </div>
      </main>
    </>
  );
}